import { auth } from '../firebase';

// Base API URL - configured per environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

// Log API URL on initialization (development only)
if (process.env.NODE_ENV !== 'production') {
  console.log('🔗 API Base URL:', API_BASE_URL);
}

// Function to get Firebase ID token
const getIdToken = async () => {
  // Check if we're in demo mode
  const isDemoMode = localStorage.getItem('demo_mode') === 'true';
  if (isDemoMode) {
    const demoToken = localStorage.getItem('demo_token');
    if (demoToken) {
      console.log('🎭 Using demo token for API requests');
      return demoToken;
    }
  }
  
  const user = auth.currentUser;
  if (user) {
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }
  return null;
};

// Generic fetch function with Firebase auth
const fetchWithAuth = async (url, options = {}) => {
  // Clone options to avoid modifying the original
  const fetchOptions = { ...options };
  
  // Ensure headers object exists
  if (!fetchOptions.headers) {
    fetchOptions.headers = {};
  }
  
  // Add Firebase ID token to Authorization header
  const idToken = await getIdToken();
  if (idToken) {
    fetchOptions.headers.Authorization = `Bearer ${idToken}`;
  }
  
  // Add Content-Type header for POST/PUT requests with body
  if ((fetchOptions.method === 'POST' || fetchOptions.method === 'PUT') && fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
    fetchOptions.headers['Content-Type'] = 'application/json';
  }
  
  // Make the request
  const response = await fetch(`${API_BASE_URL}${url}`, fetchOptions);
  
  // Handle 401 Unauthorized responses
  if (response.status === 401) {
    // Redirect to login page or handle unauthorized access
    window.location.href = '/login';
    throw new Error('Unauthorized access');
  }
  
  return response;
};

// API functions
export const api = {
  // Transactions
  getTransactions: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetchWithAuth(`/transactions${queryString ? `?${queryString}` : ''}`);
    return response.json();
  },
  
  createTransaction: async (transactionData) => {
    const response = await fetchWithAuth('/transactions', {
      method: 'POST',
      body: JSON.stringify(transactionData)
    });
    return response.json();
  },
  
  updateTransaction: async (id, transactionData) => {
    const response = await fetchWithAuth(`/transactions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(transactionData)
    });
    return response.json();
  },
  
  deleteTransaction: async (id) => {
    const response = await fetchWithAuth(`/transactions/${id}`, {
      method: 'DELETE'
    });
    return response.json();
  },
  
  // Categories
  getCategories: async () => {
    const response = await fetchWithAuth('/categories');
    return response.json();
  },
  
  createCategory: async (categoryData) => {
    const response = await fetchWithAuth('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    });
    return response.json();
  },
  
  updateCategory: async (id, categoryData) => {
    const response = await fetchWithAuth(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData)
    });
    return response.json();
  },
  
  deleteCategory: async (id) => {
    const response = await fetchWithAuth(`/categories/${id}`, {
      method: 'DELETE'
    });
    return response.json();
  },
  
  // Reports
  getReports: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetchWithAuth(`/reports${queryString ? `?${queryString}` : ''}`);
    return response.json();
  },
  
  // Credit Cards
  getCreditCardConnections: async () => {
    const response = await fetchWithAuth('/credit-cards/connections');
    return response.json();
  },
  
  createCreditCardConnection: async (connectionData) => {
    const response = await fetchWithAuth('/credit-cards/connections', {
      method: 'POST',
      body: JSON.stringify(connectionData)
    });
    return response.json();
  },
  
  deleteCreditCardConnection: async (id) => {
    const response = await fetchWithAuth(`/credit-cards/connections/${id}`, {
      method: 'DELETE'
    });
    return response.json();
  },
  
  syncCreditCardTransactions: async (id) => {
    const response = await fetchWithAuth(`/credit-cards/connections/${id}/sync`, {
      method: 'POST'
    });
    return response.json();
  },
  
  getCreditCardTransactions: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetchWithAuth(`/credit-cards/transactions${queryString ? `?${queryString}` : ''}`);
    return response.json();
  },
  
  // Users
  getCurrentUser: async () => {
    const response = await fetchWithAuth('/users/me');
    return response.json();
  },
  
  updateCurrentUser: async (userData) => {
    const response = await fetchWithAuth('/users/me', {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
    return response.json();
  }
};

export default fetchWithAuth;